/**
 * Public legal pages (Terms of Service / Privacy Policy).
 *
 * The canonical legal text lives in /public/legal/{terms,privacy}.md and is
 * fetched + rendered here with a tiny dependency-free markdown renderer that
 * covers the subset used by those documents (headings, bold/italic, ordered &
 * unordered lists, blockquotes, horizontal rules, links, paragraphs).
 */
import { useEffect, useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import MarketingHeader from "@/components/MarketingHeader";
import Logo from "@/components/Logo";

const DOCS = {
  terms: { file: "/legal/terms.md", title: "Terms of Service" },
  privacy: { file: "/legal/privacy.md", title: "Privacy Policy" },
};

// --- Inline markdown: **bold**, _italic_, [text](url) ---
function renderInline(text, keyPrefix) {
  const nodes = [];
  let remaining = text;
  let idx = 0;
  const re = /(\*\*([^*]+)\*\*|_([^_]+)_|\[([^\]]+)\]\(([^)]+)\))/;
  let m;
  while ((m = re.exec(remaining)) !== null) {
    const before = remaining.slice(0, m.index);
    if (before) nodes.push(before);
    if (m[2] !== undefined) {
      nodes.push(
        <strong key={`${keyPrefix}-b-${idx}`} className="text-white font-semibold">
          {m[2]}
        </strong>,
      );
    } else if (m[3] !== undefined) {
      nodes.push(
        <em key={`${keyPrefix}-i-${idx}`} className="text-neutral-300">
          {m[3]}
        </em>,
      );
    } else if (m[4] !== undefined) {
      nodes.push(
        <a
          key={`${keyPrefix}-a-${idx}`}
          href={m[5]}
          target="_blank"
          rel="noreferrer"
          className="text-white underline underline-offset-2 hover:text-neutral-300"
        >
          {m[4]}
        </a>,
      );
    }
    remaining = remaining.slice(m.index + m[0].length);
    idx++;
  }
  if (remaining) nodes.push(remaining);
  return nodes;
}

// --- Block-level markdown parser ---
function parseBlocks(source) {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  const isOl = (l) => /^\d+\.\s/.test(l);
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === "") {
      i++;
      continue;
    }
    if (line.startsWith("### ")) {
      blocks.push({ type: "h3", text: line.slice(4) });
      i++;
      continue;
    }
    if (line.startsWith("## ")) {
      blocks.push({ type: "h2", text: line.slice(3) });
      i++;
      continue;
    }
    if (line.startsWith("# ")) {
      blocks.push({ type: "h1", text: line.slice(2) });
      i++;
      continue;
    }
    if (line.trim() === "---") {
      blocks.push({ type: "hr" });
      i++;
      continue;
    }
    if (line.startsWith(">")) {
      const quote = [];
      while (i < lines.length && lines[i].startsWith(">")) {
        quote.push(lines[i].replace(/^>\s?/, ""));
        i++;
      }
      blocks.push({ type: "quote", text: quote.join(" ") });
      continue;
    }
    if (isOl(line)) {
      const items = [];
      while (i < lines.length && isOl(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s/, ""));
        i++;
      }
      blocks.push({ type: "ol", items });
      continue;
    }
    if (line.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].startsWith("- ")) {
        items.push(lines[i].slice(2));
        i++;
      }
      blocks.push({ type: "ul", items });
      continue;
    }
    // paragraph — accumulate until a blank line or new block starts
    const para = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      lines[i].trim() !== "---" &&
      !lines[i].startsWith("#") &&
      !lines[i].startsWith(">") &&
      !lines[i].startsWith("- ") &&
      !isOl(lines[i])
    ) {
      para.push(lines[i]);
      i++;
    }
    blocks.push({ type: "p", text: para.join(" ") });
  }
  return blocks;
}

function MarkdownView({ source }) {
  const blocks = parseBlocks(source);
  return (
    <div className="space-y-5">
      {blocks.map((b, k) => {
        const key = `blk-${k}`;
        switch (b.type) {
          case "h1":
            return (
              <h1
                key={key}
                className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white"
              >
                {renderInline(b.text, key)}
              </h1>
            );
          case "h2":
            return (
              <h2
                key={key}
                className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-white mt-10 pt-2"
              >
                {renderInline(b.text, key)}
              </h2>
            );
          case "h3":
            return (
              <h3 key={key} className="font-display text-lg font-semibold text-white mt-6">
                {renderInline(b.text, key)}
              </h3>
            );
          case "hr":
            return <hr key={key} className="border-[#1a1a1a]" />;
          case "quote":
            return (
              <blockquote
                key={key}
                className="border-l-2 border-[#3f3f46] bg-[#0d0d0d] rounded-r-md px-4 py-3 text-sm text-neutral-400 italic"
              >
                {renderInline(b.text, key)}
              </blockquote>
            );
          case "ol":
            return (
              <ol key={key} className="list-decimal pl-6 space-y-2 text-neutral-400 leading-relaxed">
                {b.items.map((it, j) => (
                  <li key={`${key}-${j}`}>{renderInline(it, `${key}-${j}`)}</li>
                ))}
              </ol>
            );
          case "ul":
            return (
              <ul key={key} className="list-disc pl-6 space-y-2 text-neutral-400 leading-relaxed">
                {b.items.map((it, j) => (
                  <li key={`${key}-${j}`}>{renderInline(it, `${key}-${j}`)}</li>
                ))}
              </ul>
            );
          default:
            return (
              <p key={key} className="text-neutral-400 leading-relaxed">
                {renderInline(b.text, key)}
              </p>
            );
        }
      })}
    </div>
  );
}

export default function Legal({ doc = "terms" }) {
  const meta = DOCS[doc] || DOCS.terms;
  const [content, setContent] = useState("");
  const [status, setStatus] = useState("loading"); // loading | ok | error

  useEffect(() => {
    let active = true;
    setStatus("loading");
    fetch(meta.file, { cache: "no-cache" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => {
        if (!active) return;
        setContent(text);
        setStatus("ok");
      })
      .catch(() => {
        if (active) setStatus("error");
      });
    // scroll to top on doc change
    window.scrollTo(0, 0);
    return () => {
      active = false;
    };
  }, [meta.file]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <MarketingHeader showSubscribe={true} showSignIn={true} showAudit={true} />

      <main className="flex-1 w-full">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <a
            href="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.8} />
            Back to home
          </a>

          <div className="mt-8" data-testid={`legal-${doc}`}>
            {status === "loading" && (
              <div className="flex items-center gap-2 text-neutral-500 text-sm font-mono py-16">
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
                Loading {meta.title}…
              </div>
            )}
            {status === "error" && (
              <div className="py-16 text-neutral-400">
                <h1 className="font-display text-2xl font-semibold text-white">{meta.title}</h1>
                <p className="mt-4 text-sm">
                  We couldn't load this document right now. Please try again shortly.
                </p>
              </div>
            )}
            {status === "ok" && <MarkdownView source={content} />}
          </div>
        </div>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-[#161616]">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4">
          <Logo variant="white" className="h-5 opacity-70" />
          <div className="flex items-center gap-5 text-xs font-mono uppercase tracking-[0.2em] text-neutral-500">
            <a href="/" className="hover:text-white" data-testid="legal-footer-home">
              Home
            </a>
            <a href="/terms" className="hover:text-white" data-testid="legal-footer-terms">
              Terms
            </a>
            <a href="/privacy" className="hover:text-white" data-testid="legal-footer-privacy">
              Privacy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
